import {getErrorCode} from './errorCode'

export type OrderErrorContext = 'read' | 'write'

/**
 * Maps a TRPCClientError (or any error-like value) to a clear Spanish copy.
 *
 * `context` picks the copy flavour:
 *   - 'write' (default) — the weekly proposal editLines interaction: copy talks
 *     about editing/saving. Used by ProposalPage.
 *   - 'read' — the read-only order-history list/detail screens, which show no
 *     edit/save affordance: copy talks about viewing/loading instead. Used by
 *     OrderHistoryPage/OrderDetailPage.
 *
 * Only the action-specific branches (FORBIDDEN, generic fallback) vary by
 * context — cutoff/carta/NOT_FOUND describe the ORDER, not the action, so their
 * copy is the same in both contexts.
 *
 * The TRPCError code decides, and the message is only consulted where the
 * backend has no code left to distinguish two cases with — inside FORBIDDEN
 * (deactivated store vs missing role) and inside BAD_REQUEST (cutoff passed vs
 * product off the carta). Matching a message across codes was fragile in both
 * directions: the order id is interpolated into NOT_FOUND messages, and any
 * unrelated failure that happened to name the carta was read as an out-of-menu
 * error.
 *
 * The function accepts `unknown` so callers don't have to cast.
 */
export const mapOrderError = (err: unknown, context: OrderErrorContext = 'write'): string => {
  const message: string =
    err !== null &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as {message: unknown}).message === 'string'
      ? (err as {message: string}).message.toLowerCase()
      : ''

  const generic =
    context === 'read'
      ? 'Se produjo un error al cargar la información. Inténtalo de nuevo.'
      : 'Se produjo un error al guardar el pedido. Inténtalo de nuevo.'

  switch (getErrorCode(err)) {
    // VIEWER attempting to edit ('write') / no access to view ('read'), or the
    // store's write lock — both arrive as FORBIDDEN, so the backend's
    // "Store is deactivated" is the only thing telling them apart.
    case 'FORBIDDEN':
      if (message.includes('deactivated')) {
        return 'Esta tienda está desactivada; no admite pedidos.'
      }
      return context === 'read'
        ? 'No tienes permiso para ver este pedido.'
        : 'No tienes permiso para editar este pedido.'

    case 'NOT_FOUND':
      return 'No se encontró el pedido. Es posible que haya sido eliminado.'

    // The editLines rejections share this code; again only the message splits them.
    case 'BAD_REQUEST':
      if (message.includes('cutoff')) {
        // No hour named: each store closes at its own cutoff, and the countdown
        // beside the order is what says when this one did.
        return 'Se ha pasado la hora de corte. El pedido queda bloqueado.'
      }
      if (message.includes('not in the carta')) {
        return 'Algún producto ya no está en la carta de esta semana.'
      }
      // The store's floor rose while this tab was open: its selector still
      // enforces the OLD minimum, so retrying can never succeed — reloading is
      // what fetches the new one.
      if (message.includes('below the store\'s minimum')) {
        return 'Alguna línea queda por debajo del mínimo por producto de tu tienda. Recarga la página para ver el mínimo vigente.'
      }
      return generic

    default:
      return generic
  }
}
